// src/views/solutionBuilder/SolutionBuilder.js
import { useState, useCallback, useMemo, useEffect } from "react";
import BoxedLayout from "../../parts/layout/BoxedLayout";
import { StepIndicator } from "../../components/solutionBuilder/StepIndicator";
import { BusinessInfoStep } from "../../components/solutionBuilder/BusinessInfoStep";
import { ChannelsStep } from "../../components/solutionBuilder/ChannelsStep";
import { PaymentMethodsStep } from "../../components/solutionBuilder/PaymentMethodsStep";
import { IntegrationStep } from "../../components/solutionBuilder/IntegrationStep";
import { ReviewStep } from "../../components/solutionBuilder/ReviewStep";
import ChatComponent from "../../components/ai/ChatComponent";
import QuestionGenerator from "../../components/ai/QuestionGenerator";
import { FiArrowLeft, FiArrowRight, FiSend, FiSettings } from "react-icons/fi";


const questionsForTiles = [
    "Generate SCQA response",
    "Generate an email for the customer of the solution",
    "Generate a 2-minute elevator pitch"
];

function SolutionBuilder() {
    const [currentStep, setCurrentStep] = useState(0);
    const [showChat, setShowChat] = useState(false);
    const [question, setQuestion] = useState(""); // This is the main question state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        businessName: '',
        website: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        businessSector:'',
        region:'',
        country:'',
        expectedMonthlyVolume: '',
        opportunityBackground: '',
        currentAcquirers: '',
        channels: [],
        paymentMethods: [],
        cardSchemes: [],
        terminalTypes: [],
        ecommerceFeatures: [],
        bnplProviders: [],
        wallets: [],
        integrationTypes: [],
        notes: '',
    });
    const [errors, setErrors] = useState({});

    const updateFormField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        
        // Clear error for this field if it exists
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = {...prev};
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const toggleArrayField = (field, item) => {
        setFormData((prev) => {
            const currentArray = prev[field];
            return {
                ...prev,
                [field]: currentArray.includes(item)
                    ? currentArray.filter((i) => i !== item)
                    : [...currentArray, item],
            };
        });
    };

    const validateCurrentStep = () => {
        if (currentStep === 0) {
            // Validate BusinessInfoStep
            const newErrors = {};
            
            if (!formData.region) {
                newErrors.region = "Please select a region";
            }

            if (!formData.businessSector) {
                newErrors.businessSector = "Please select a business sector";
            }
            
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }
        
        return true; // No validation for other steps in this example
    };

    const handleNext = () => {
        const isValid = validateCurrentStep();
        
        if (isValid) {
            setCurrentStep((prev) => Math.min(prev + 1, 4));
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    // Function to generate question from form data
    const generateQuestionFromFormData = (data) => {
        let question = "Based the potential customers business requirements: ";
        
        if (data.businessName) {
            question += `Business: ${data.businessName}, `;
        }
        
        if (data.businessSector) {
            question += `Sector: ${data.businessSector}, `;
        }
        
        if (data.region) {
            question += `Region: ${data.region}, `;
        }

        if (data.country){
            question += `Country: ${data.country}, `;
        }
        
        if (data.expectedMonthlyVolume) {
            question += `Expected Monthly Volume: ${data.expectedMonthlyVolume}, `;
        }

        if (data.opportunityBackground) {
            question += `Opportunity Background: ${data.opportunityBackground}, `;
        }

        if (data.currentAcquirers) {
            question += `Current Acquirers/Processors: ${data.currentAcquirers}, `;
        }
        
        if (data.channels.length > 0) {
            question += `Channels: ${data.channels.join(', ')}, `;
        }
        
        if (data.paymentMethods.length > 0) {
            question += `Payment Methods: ${data.paymentMethods.join(', ')}, `;
        }
        
        if (data.cardSchemes.length > 0) {
            question += `Card Schemes: ${data.cardSchemes.join(', ')}, `;
        }
        
        if (data.wallets.length > 0) {
            question += `Wallets: ${data.wallets.join(', ')}, `;
        }
        
        if (data.integrationTypes.length > 0) {
            question += `Integration Types: ${data.integrationTypes.join(', ')}, `;
        }

        if (data.terminalTypes.length > 0) {
            question += `Terminal Requirements: ${data.terminalTypes.join(', ')}, `;
        }
        
        // Remove trailing comma and space
        question = question.replace(/, $/, '');
        
        question += ". Include detail, including aquirer certifications (if a terminal is not certified with an acquirer make it clear), payment type support, peripherals, power supply, mounting, modules if required for business sector, anything that is relevant. What payment solution would you recommend? ";
        
        return question;
    };

    const handleSubmit = async () => {
        // Prevent multiple submissions
        if (isSubmitting) return;
        
        try {
            setIsSubmitting(true);
            console.log('Form submitted:', formData);
            
            // Generate the question based on form data and set it
            const generatedQuestion = generateQuestionFromFormData(formData);
            setQuestion(generatedQuestion); // Set the main question state
            
            // Show the chat component
            setShowChat(true);
            
            console.log('Generated question:', generatedQuestion);
            
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to submit merchant details. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleQuestionSelect = (questionText) => {
        setQuestion(questionText + " " + question);
    };

    const handleBackToForm = () => {
        setShowChat(false);
        setQuestion(""); // Clear the question
        setIsSubmitting(false);
    };

    // Memoize steps to prevent unnecessary re-renders
    const steps = useMemo(() => [
        <BusinessInfoStep
            key="business"
            formData={formData}
            onChange={updateFormField}
            errors={errors}
        />,
        <ChannelsStep
            key="channels"
            selectedChannels={formData.channels}
            onToggleChannel={(channel) =>
                toggleArrayField('channels', channel)
            }
        />,
        <PaymentMethodsStep
            key="payment"
            selectedRegion={formData.region}
            selectedCountry={formData.country}
            selectedBusinessSector={formData.businessSector}
            selectedMethods={formData.paymentMethods}
            selectedCardSchemes={formData.cardSchemes}
            selectedChannels={formData.channels}
            selectedWallets={formData.wallets}
            selectedTerminalTypes={formData.terminalTypes}
            selectedEcommerceFeatures={formData.ecommerceFeatures}
            selectedBnplProviders={formData.bnplProviders}
            onToggleMethod={(method) =>
                toggleArrayField('paymentMethods', method)
            }
            onToggleScheme={(scheme) =>
                toggleArrayField('cardSchemes', scheme)
            }
            onToggleWallet={(wallet) =>
                toggleArrayField('wallets', wallet)
            }
            onToggleTerminalType={(type) =>
                toggleArrayField('terminalTypes', type)
            }
            onToggleEcommerceFeature={(feature) =>
                toggleArrayField('ecommerceFeatures', feature)
            }
            onToggleBnplProvider={(provider) =>
                toggleArrayField('bnplProviders', provider)
            }
        />,
        <IntegrationStep
            key="integration"
            selectedIntegrations={formData.integrationTypes}
            onToggleIntegration={(integration) =>
                toggleArrayField('integrationTypes', integration)
            }
        />,
        <ReviewStep key="review" data={formData} />,
    ], [formData, errors]);

    // If showChat is true, render the ChatComponent instead of the form
    if (showChat) {
        return (
            <BoxedLayout
                content={
                    <>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                                <FiSettings className="w-4 h-4 mr-2" />
                                AI-Powered Solution Builder
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                                Build your
                                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> payment solution</span>
                            </h1>
                        </div>
                       
                        <div className="mb-4">
                            <button
                                onClick={handleBackToForm}
                                className="flex items-center px-4 py-2 bg-gray-500 text-white hover:bg-white hover:text-gray-600 transition-colors border border-gray-400 rounded-full text-sm font-medium"
                            >
                                <FiArrowLeft className="w-4 h-4 mr-2" />
                                Back to Solution Builder
                            </button>
                        </div>
                        
                        <div className="w-full max-w-5xl bg-white bg-opacity-80 p-6 rounded-lg shadow-lg">
                             <QuestionGenerator 
                                questions={questionsForTiles} 
                                onQuestionSelect={handleQuestionSelect} 
                            />
                            <ChatComponent 
                                question={question} 
                                region={formData.region}
                            />
                        </div>
                    </>
                }
            />
        );
    }

    return (
        <BoxedLayout
            content={
                <>
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <FiSettings className="w-4 h-4 mr-2" />
                            AI-Powered Solution Builder
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                            Build your
                            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> payment solution</span>
                        </h1>
                    </div>

                    <StepIndicator currentStep={currentStep} totalSteps={5} />

                    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                        {steps[currentStep]}
                    </div>

                    <div className="flex justify-between">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 0}
                            className={`flex items-center px-6 py-3 rounded-lg ${
                                currentStep === 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <FiArrowLeft className="w-5 h-5 mr-2" />
                            Back
                        </button>

                        <button
                            onClick={currentStep === steps.length - 1 ? handleSubmit : handleNext}
                            disabled={isSubmitting}
                            className={`flex items-center px-6 py-3 rounded-lg ${
                                isSubmitting 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}
                        >
                            {currentStep === steps.length - 1 ? (
                                <>
                                    {isSubmitting ? 'Loading...' : 'Get AI Recommendation'}
                                    <FiSend className="w-5 h-5 ml-2" />
                                </>
                            ) : (
                                <>
                                    Next
                                    <FiArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </button>
                    </div>
                </>
            }
        />
    );
}

export default SolutionBuilder;